import { AvatarTypes, ApiTypes, StateRecord } from '@mode/shared/contract-common';
import { OrgTypes } from '@mode/shared/contract-common';
import { ModeResourceHelper } from '@mode/shared/util-halogen';
import { OrganizationFeatureResource } from './organization-feature.resource';
import { GroupResource } from './group.resource';

export interface OrganizationResource extends StateRecord, ApiTypes.ModeResource {
  authorized_domains: string[];
  avatar: AvatarTypes.Avatars;
  membership_type: 'admin' | 'full' | 'limited';
  name: string;
  payment_method_confirmed: boolean;
  plan_code: OrgTypes.PlanCode;
  private_definition_count: number;
  private_definition_limit: string;
  space_count: number;
  token: string;
  trial_state: string;
  user: boolean;
  username: string;
  org_seat_limit_reached: boolean;
  dataset_size_limit_mb: number;
  scim_enabled?: boolean;
  thoughtspot_integrated?: boolean;
  settings: { [key: string]: boolean };
  python_performance_first_tier: boolean;
  organization_restricted: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace OrganizationHelpers {
  import isResource = ModeResourceHelper.isResource;

  export function isCommunityOrg(resource: OrganizationResource): boolean {
    return resource.user;
  }

  export function IsScimEnabled(resource: OrganizationResource): boolean {
    return isResource(resource) && resource.scim_enabled === true;
  }

  export function IsThoughtspotIntegrated(resource: OrganizationResource): boolean {
    return isResource(resource) && resource.thoughtspot_integrated === true;
  }

  export function canCreateDatasources(resource: OrganizationResource): boolean {
    return isResource(resource) && resource.hasLink('web_data_sources_settings');
  }

  export function hasSpaceDirectory(resource: OrganizationResource): boolean {
    return isResource(resource) && resource.hasLink('web_spaces');
  }

  export function getTrialLink(resource: OrganizationResource): string | null {
    return (
      (isResource(resource) &&
        resource.hasLink('web_trial_appointments') &&
        resource.links('web_trial_appointments').href) ||
      null
    );
  }

  export function getNewInviteLink(resource: OrganizationResource): string | null {
    return (
      (isResource(resource) && resource.hasLink('new_invite_web') && resource.links('new_invite_web').href) || null
    );
  }

  export function getMembershipEventsLink(resource: OrganizationResource): string | null {
    return (
      (isResource(resource) &&
        resource.hasLink('web_membership_events') &&
        resource.links('web_membership_events').href) ||
      null
    );
  }

  export function getMemberSessionsLink(resource: OrganizationResource): string | null {
    return (
      (isResource(resource) && resource.hasLink('web_member_sessions') && resource.links('web_member_sessions').href) ||
      null
    );
  }

  export function getOrgFeatures(resource: OrganizationResource): OrganizationFeatureResource {
    if (isResource(resource) && resource.hasEmbedded('organization_features')) {
      return resource.embedded('organization_features');
    } else {
      return null;
    }
  }

  export function getOrgSpace(resource: OrganizationResource): OrganizationFeatureResource {
    if (isResource(resource) && resource.hasEmbedded('organization_space')) {
      return resource.embedded('organization_space');
    } else {
      return null;
    }
  }

  export function getSelectDataSourcesLink(resource: OrganizationResource): string | null {
    return (
      (isResource(resource) &&
        resource.hasLink('select_data_sources_web') &&
        resource.links('select_data_sources_web').href) ||
      null
    );
  }

  export function getMembershipType(resource: OrganizationResource): 'admin' | 'full' | 'limited' | null {
    return (isResource(resource) && resource.membership_type) || null;
  }

  export function isOrgAdmin(resource: OrganizationResource): boolean {
    return getMembershipType(resource) === 'admin';
  }

  export function hasScimToken(resource: OrganizationResource): boolean {
    return isResource(resource) && resource.hasLink('scim_token');
  }

  export function isOrgEveryoneGroup(resource: OrganizationResource, group: GroupResource): boolean {
    return (
      isResource(resource) &&
      resource.hasLink('everyone_group') &&
      resource.links('everyone_group').href === group.links('self').href
    );
  }
}
